/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  InteractivityHotspotActionTypes,
  useInteracitvityHotspotDispatch,
} from "../../contexts/InteractivityHotspotProvider";
import { useObjectsState, ObjectActionsType, useObjectsDispatch } from "../../contexts/ObjectsProvider";
import { useObjectPosition } from "../../hooks/useObjectPosition";
import { ReactComponent as Icon } from "../../assets/icons/AssetManager/smart-object.svg";
import styles from "./SmartObject.module.css";
import { SmartObjectRenderer } from "../../lib/SmartObject/components/SmartObjectRenderer";
import React, { useEffect, useMemo, useRef, useContext } from "react";
import { objectOpacityFromRules } from "../../utils";
import { parseClipPath, createCroppedBorderStyle } from "../../utils/clipPathUtils";
import { useObjectIsInTime } from "../../hooks/useObjectIsInTime";
import { useLessonPagesState } from "../../contexts/LessonPagesProvider/LessonPagesProvider";
import { useMovableElementsPlaneState } from "../../contexts/MovableElementsPlaneProvider";
import { useSelectedObjectDispatch } from "../../contexts/SelectedObjectProvider/SelectedObjectProvider";
import { useMetaVariableStore } from "../../lib/SmartObject/store";
import { addSmartObjectToPage } from "../../lib/SmartObject/addSmartObjectToPage";
import { PageContext } from "../../routes/builderContexts";
import genericRepositoryService from "../../services/genericRepositoryService";

interface SmartObjectsProps {
  handleDoubleClick: (e: React.MouseEvent<HTMLDivElement | HTMLVideoElement>) => void;
  replacedAssets: { assetVersionId: number }[];
}

const CROPPED_SMO_STYLES: React.CSSProperties = {
  zIndex: 1,
  position: "relative",
} as const;

export const SmartObjects = ({ handleDoubleClick, replacedAssets }: SmartObjectsProps) => {
  const { smartObjects } = useObjectsState();

  // Hydrate incomplete SmartObjects and migrate missing assetBlobPath
  const { viewportDOMElementWidth, viewportDOMElementHeight } = useMovableElementsPlaneState();
  const addNewMetaVariable = useMetaVariableStore((s) => s.addNewMetaVariable);
  const clearObjectMetaVariables = useMetaVariableStore((s) => s.clearObjectMetaVariables);
  const objectsDispatch = useObjectsDispatch();
  const selectedObjectDispatch = useSelectedObjectDispatch();
  const pageContext = useContext(PageContext);
  const ranRef = useRef(false);

  useEffect(() => {
    // Only for Base FreeForm pages and when viewport is known
    if (pageContext.pageManifest?.basePageType !== "freeForm") return;
    if (!viewportDOMElementWidth || !viewportDOMElementHeight) return;

    // console.debug("SmartObjects mount effect triggered");

    // Avoid re-running the full routine more than needed during mount
    if (ranRef.current) return;
    ranRef.current = true;

    const doWork = async () => {
      // console.debug("Starting SmartObject migration and hydration process");

      // 1) Migration: add assetBlobPath for legacy SmartObjects missing it
      const missingBlobPath = smartObjects.filter(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (so: any) => !!so?.assetVersionId && !so?.assetBlobPath,
      );

      if (missingBlobPath.length > 0) {
        // console.debug("Migrating SmartObjects to add missing assetBlobPath", missingBlobPath);

        await Promise.all(
          missingBlobPath.map(async (so) => {
            try {
              // Prefer legacy inline blobPath when present to avoid an API call
              const legacyBlobPath = (so as any)?.blobPath as string | undefined;
              let resolvedBlobPath: string | undefined = legacyBlobPath;

              if (!resolvedBlobPath) {
                const meta = await genericRepositoryService.getAssetMetaData(so.assetVersionId);
                // Try common shapes from API responses
                resolvedBlobPath = meta?.data?.asset?.blobPath ?? meta?.data?.blobPath ?? meta?.blobPath;
              }

              if (resolvedBlobPath) {
                objectsDispatch({
                  type: ObjectActionsType.UPDATE_OBJECT,
                  payload: {
                    objectId: so.objectId,
                    object: { assetBlobPath: resolvedBlobPath } as any,
                  },
                });
              }
            } catch (e) {
              // no-op; hydration may still work later if data shows up
              // console.warn("Failed to migrate assetBlobPath for", so.objectId, e);
            }
          }),
        );
      }

      // 2) Hydration: for SmartObjects without elementList but with essentials
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const toHydrate = smartObjects.filter((so: any) => {
        const hasStructure = Array.isArray(so?.elementList) && so.elementList.length > 0;
        const hasEssentials = !!so?.assetVersionId && (!!so?.assetBlobPath || !!so?.blobPath);
        return !hasStructure && hasEssentials;
      });

      for (const so of toHydrate) {
        // console.debug("Hydrating SmartObject", so?.objectId, so);
        try {
          await addSmartObjectToPage({
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            selectedObject: so as any,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            blobPath: (so as any).assetBlobPath ?? (so as any).blobPath,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            assetVersionId: (so as any).assetVersionId,
            viewport: { width: viewportDOMElementWidth, height: viewportDOMElementHeight },
            addNewMetaVariable,
            clearObjectMetaVariables,
            objectsDispatch,
            selectedObjectDispatch,
          });
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error("Failed to hydrate SmartObject", so?.objectId, err);
        }
      }
    };

    void doWork();
  }, [smartObjects, viewportDOMElementWidth, viewportDOMElementHeight, pageContext.pageManifest?.basePageType]);

  return smartObjects.map((item) => (
    <SmartObject
      key={item.objectId}
      handleDoubleClick={handleDoubleClick}
      highlightedBorder={replacedAssets?.some(
        (asset: { assetVersionId: number }) => asset.assetVersionId === item.assetVersionId,
      )}
      {...item}
    />
  ));
};

interface SmartObjectProps {
  objectId: string;
  left: number;
  top: number;
  width: number;
  height: number;
  ghost?: boolean;
  /**@deprecated */
  rotate?: number;
  rotation?: number;
  opacity?: number;
  pitch?: number;
  yaw?: number;
  isDisplayed?: boolean;
  zIndex?: number;
  elementList?: any;
  clipPath?: {
    absoluteTop: number;
    absoluteLeft: number;
    absoluteRight: number;
    absoluteBottom: number;
    bottom: number;
    left: number;
    right: number;
    top: number;
  };
  handleDoubleClick: (e: React.MouseEvent<HTMLDivElement | HTMLVideoElement>) => void;
  highlightedBorder?: boolean;
  clipPathString?: string;
  transformOrigin?: {
    x: number;
    y: number;
  };
}

const SmartObject = ({
  objectId,
  left,
  top,
  width,
  height,
  rotation,
  opacity,
  zIndex,
  elementList,
  isDisplayed,
  ghost = false,
  clipPath,
  handleDoubleClick,
  clipPathString,
  transformOrigin,
  highlightedBorder,
}: SmartObjectProps) => {
  const {
    position: [x, y],
    rotation: finalRotation,
    size: [widthWithFrames, heightWithFrames],
    opacity: opacityValue,
  } = useObjectPosition(objectId, top, left, width, height, rotation, opacity);
  const objectDispatch = useObjectsDispatch();
  const objectsState = useObjectsState();
  const hotspotsDispatch = useInteracitvityHotspotDispatch();
  const { selectedPanel } = useLessonPagesState();

  const rotateString = typeof finalRotation === "number" ? `rotate(${finalRotation}deg)` : "rotate(0deg)";

  const transform = `translate(${x}px, ${y}px) ${rotateString}`;
  const transformOriginString = transformOrigin ? `${transformOrigin.x}% ${transformOrigin.y}%` : undefined;
  const isInTime = useObjectIsInTime(objectId);
  const finalOpacity = objectOpacityFromRules(opacityValue, isDisplayed, isInTime, ghost);
  const isCroppedImage = clipPathString ? true : false;
  const isCropping = objectsState.isCropping;
  const smartObject = useMemo(() => {
    return {
      objectId,
      height: heightWithFrames,
      width: widthWithFrames,
      left,
      top,
      elementList,
    };
  }, [objectId, heightWithFrames, widthWithFrames, top, left, elementList]);

  const {
    top: topFromClipPath = 0,
    right: rightFromClipPath = 0,
    bottom: bottomFromClipPath = 0,
    left: leftFromClipPath = 0,
  } = clipPath ?? {};

  const clipContainerLeft = leftFromClipPath;
  const clipContainerTop = topFromClipPath;
  const clipContainerWidth = 100 - leftFromClipPath - rightFromClipPath;
  const clipContainerHeight = 100 - topFromClipPath - bottomFromClipPath;

  const shouldContainerBeMoveable = useMemo(() => {
    if (isCroppedImage && isCropping) {
      objectsState.moveableRef?.updateRect();
      return true;
    }
    if (isCroppedImage) {
      objectsState.moveableRef?.updateRect();
      return false;
    }
    return true;
  }, [isCroppedImage, isCropping, objectsState.moveableRef]);

  useEffect(() => {
    if (shouldContainerBeMoveable) {
      setTimeout(() => {
        objectsState.moveableRef?.updateRect();
      }, 300);
    }
  }, [shouldContainerBeMoveable, objectsState.moveableRef]);

  const extraSmartObjectStyles =
    !shouldContainerBeMoveable && selectedPanel === "advanced" ? CROPPED_SMO_STYLES : undefined;

  const visibleArea = parseClipPath(clipPathString || "", widthWithFrames, heightWithFrames);

  return (
    <div
      aria-label="Smart Object"
      className={elementList ? "" : styles.empty}
      data-objectId={shouldContainerBeMoveable ? objectId : ""}
      data-ref={objectId}
      style={{
        position: "absolute",
        width: `${widthWithFrames}px`,
        height: `${heightWithFrames}px`,
        opacity: finalOpacity,
        zIndex,
        transform,
        transformOrigin: transformOriginString,
        rotate: `${rotateString}`,
        display: isInTime || ghost ? undefined : "none",
        clipPath: isCroppedImage ? clipPathString : "",
        ...(highlightedBorder && !isCroppedImage
          ? { border: "2px solid var(--confirm-review-color)", borderRadius: "4px" }
          : {}),
      }}
      onMouseDown={(e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        hotspotsDispatch({
          type: InteractivityHotspotActionTypes.SET_CURRENT_HOTSPOT,
          payload: null,
        });
        if (!objectId) return;
        let type = ObjectActionsType.SET_SELECTED_OBJECT;
        if (e.ctrlKey) {
          type = ObjectActionsType.ADD_SELECTED_OBJECT;
        }
        objectDispatch({
          type,
          payload: { objectId },
        });
      }}
      onDoubleClick={(e: React.MouseEvent<HTMLDivElement>) => handleDoubleClick(e)}
    >
      {isCroppedImage && highlightedBorder && visibleArea && (
        <div aria-label="Cropped smart object border" style={createCroppedBorderStyle(visibleArea)} />
      )}
      {elementList ? (
        <div
          style={{
            width: "100%",
            height: "100%",
            position: "relative",
          }}
        >
          <SmartObjectRenderer smartObject={smartObject as unknown as any} extraStyles={extraSmartObjectStyles} />
          {!shouldContainerBeMoveable && (
            <CroppedMoveableContainer
              objectId={objectId}
              clipContainerTop={clipContainerTop}
              clipContainerLeft={clipContainerLeft}
              clipContainerWidth={clipContainerWidth}
              clipContainerHeight={clipContainerHeight}
              shouldContainerBeMoveable={shouldContainerBeMoveable}
            />
          )}
        </div>
      ) : (
        <span className={styles.icon}>
          <Icon
            style={{
              width: "80%",
              height: "80%",
            }}
          />
        </span>
      )}
    </div>
  );
};

export function CroppedMoveableContainer({
  objectId,
  clipContainerTop,
  clipContainerLeft,
  clipContainerWidth,
  clipContainerHeight,
  shouldContainerBeMoveable,
}: {
  objectId: string;
  clipContainerTop: number;
  clipContainerLeft: number;
  clipContainerWidth: number;
  clipContainerHeight: number;
  shouldContainerBeMoveable: boolean;
}) {
  const objectDispatch = useObjectsDispatch();
  const hotspotsDispatch = useInteracitvityHotspotDispatch();
  return (
    <div
      data-objectId={!shouldContainerBeMoveable ? objectId : ""}
      onMouseDown={(e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        hotspotsDispatch({
          type: InteractivityHotspotActionTypes.SET_CURRENT_HOTSPOT,
          payload: null,
        });
        if (!objectId) return;
        let type = ObjectActionsType.SET_SELECTED_OBJECT;
        if (e.ctrlKey) {
          type = ObjectActionsType.ADD_SELECTED_OBJECT;
        }
        objectDispatch({
          type,
          payload: { objectId },
        });
      }}
      style={{
        // pointerEvents: "none",
        position: "absolute",
        height: clipContainerHeight + "%",
        width: clipContainerWidth + "%",
        top: clipContainerTop + "%",
        left: clipContainerLeft + "%",
      }}
    ></div>
  );
}
